/**
 * ShareProfileButton — prominent, one-tap "Share my profile" CTA for the
 * instructor dashboard. Turns every active verified instructor into a
 * referral-driven growth channel.
 *
 * - Pulls the instructor's marketplace profile (rate, area, eligibility).
 * - If eligible + visible → "Share my profile" button uses native share
 *   with a clipboard fallback.
 * - If eligible but not visible → nudge to enable marketplace listing.
 * - If not eligible → hidden entirely (the share link would 404).
 *
 * Share URL points to the OG HTML endpoint so every paste into WhatsApp /
 * iMessage / Facebook / LinkedIn / X renders the dynamic branded card.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Copy, CheckCircle2, Sparkles, Store } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function buildCaption(profile) {
  const parts = [];
  const area =
    profile?.area ||
    (profile?.postcode ? profile.postcode.split(/\s+/)[0].toUpperCase() : null);
  if (area) parts.push(area);
  const rateStr = profile?.hourly_rate ? `from £${profile.hourly_rate}/hr` : null;

  const where = parts.length ? parts.join(", ") : "";
  const tail = [where, rateStr].filter(Boolean).join(", ");
  const base = "Book driving lessons with me on National Driving Guild";
  return tail ? `${base} — ${tail}` : base;
}

function buildShareUrl(userId) {
  const base = (API_URL || "").replace(/\/+$/, "");
  // OG HTML endpoint: social crawlers render rich preview, humans get redirected
  return base
    ? `${base}/api/marketplace/og/instructor/${userId}`
    : `/api/marketplace/og/instructor/${userId}`;
}

export default function ShareProfileButton() {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/marketplace/my-share-stats`, {
        withCredentials: true,
      });
      setStats(data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/marketplace/my-profile`, {
          withCredentials: true,
        });
        if (!cancelled) setProfile(data);
      } catch {
        /* Non-instructor or unauthenticated — component self-hides */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  const logShareEvent = async (channel) => {
    try {
      await axios.post(
        `${API_URL}/api/marketplace/share-event`,
        { channel },
        { withCredentials: true },
      );
      fetchStats();
    } catch {
      /* Non-blocking — growth metric, not core flow */
    }
  };

  if (loading || !profile) return null;

  const eligible = profile?.eligibility?.eligible;
  // Hide completely for ineligible users — sharing a link would 404
  if (!eligible) return null;

  const visible = !!profile.visible;
  const caption = buildCaption(profile);
  const shareUrl = buildShareUrl(profile.user_id);
  const shareText = `${caption}\n${shareUrl}`;

  // Not yet listed → gentle nudge CTA
  if (!visible) {
    return (
      <Link
        to="/settings/marketplace"
        data-testid="share-profile-nudge"
        className="block bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-900 rounded-2xl border border-slate-800 p-4 sm:p-5 text-white hover:from-slate-800 hover:to-indigo-800 transition-all group"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Store className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">List on the marketplace to unlock sharing</p>
            <p className="text-xs text-white/60 mt-0.5">
              Turn your profile into a referral channel · takes 30 seconds
            </p>
          </div>
          <span className="text-white/50 group-hover:text-white transition-colors text-sm">→</span>
        </div>
      </Link>
    );
  }

  const handleShare = async () => {
    // Native share (mobile + newer desktop browsers)
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "National Driving Guild",
          text: caption,
          url: shareUrl,
        });
        logShareEvent("native");
        return;
      } catch (err) {
        // User cancelled → silent. Any other error → fall through to clipboard.
        if (err && err.name === "AbortError") return;
      }
    }

    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Share link copied — paste it in WhatsApp, Instagram, anywhere.");
      logShareEvent("copy");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy. Please copy this link manually: " + shareUrl);
    }
  };

  const handleCopyOnly = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Copied to clipboard");
      logShareEvent("copy");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — select the link below.");
    }
  };

  return (
    <div
      data-testid="share-profile-card"
      className="relative overflow-hidden rounded-2xl border border-indigo-900/60 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-4 sm:p-5 text-white"
    >
      {/* Sparkle accent */}
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-900/40">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-indigo-300">
            Grow your pupil list
          </p>
          <h3 className="text-lg font-bold text-white leading-tight mt-0.5">Share my profile</h3>
          <p className="text-xs text-white/60 mt-1">
            One-tap share with a branded preview card. Paste anywhere.
          </p>
        </div>
      </div>

      {/* Preview caption */}
      <div
        data-testid="share-profile-caption"
        className="relative bg-black/30 border border-white/10 rounded-xl p-3 text-sm text-white/90 mb-4 leading-relaxed"
      >
        <p>{caption}</p>
        <p className="text-indigo-300 text-xs mt-1 truncate">{shareUrl}</p>
      </div>

      <div className="relative flex gap-2">
        <button
          type="button"
          onClick={handleShare}
          data-testid="share-profile-btn"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-slate-900 font-semibold text-sm rounded-xl px-4 py-3 hover:bg-slate-50 active:scale-[0.98] transition-all shadow-lg shadow-black/20"
        >
          <Share2 className="w-4 h-4" />
          Share my profile
        </button>
        <button
          type="button"
          onClick={handleCopyOnly}
          data-testid="share-profile-copy"
          className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white font-semibold text-sm rounded-xl px-4 py-3 hover:bg-white/15 active:scale-[0.98] transition-all"
          title="Copy link to clipboard"
        >
          {copied ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-300" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>

      {stats && (
        <p
          data-testid="share-profile-stats"
          className="relative text-[11px] text-white/50 mt-3 text-center"
        >
          Shares this month:{" "}
          <span className="text-white/80 font-semibold">{stats.shares_this_month}</span>
          {stats.shares_all_time > stats.shares_this_month && (
            <span className="text-white/30"> · {stats.shares_all_time} all-time</span>
          )}
        </p>
      )}
    </div>
  );
}
